import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { z } from 'zod';
import { FEEDBACK_AREAS, FEEDBACK_KINDS, FEEDBACK_SEVERITIES, FEEDBACK_STATUSES, syncFeedback } from '@mkcore/dev/schema';
import { Badge, Button, EmptyState, ErrorState, Field, Input, Mono, NativeSelect, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr, toast } from '@mkcore/ui';
import { formatDate } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { feedbackConfigQuery, feedbackListQuery, feedbackSummaryQuery } from '@/features/feedback/queries';
import { FeedbackDialog } from '@/components/feedback-dialog';
import { KindLabel, SeverityText, StatusBadge } from '@/features/feedback/labels';

const search = z.object({
  status: z.enum([...FEEDBACK_STATUSES, 'open', 'any']).optional(),
  kind: z.enum(FEEDBACK_KINDS).optional(),
  severity: z.enum(FEEDBACK_SEVERITIES).optional(),
  area: z.enum(FEEDBACK_AREAS).optional(),
  source: z.enum(['app', 'forge']).optional(),
  q: z.string().optional(),
  scope: z.enum(['mine', 'all']).optional(),
});

export const Route = createFileRoute('/_app/feedback/')({ validateSearch: search, component: FeedbackList });

/** The feedback log (CO-0398): a local copy of the Forgejo issues, the team sees all, others their own. */
function FeedbackList() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const message = useErrorMessage();
  const manager = useCan('feedback:read');
  const config = useQuery(feedbackConfigQuery);
  const [open, setOpen] = useState(false);
  const status = s.status ?? 'open';
  const q = useInfiniteQuery(
    feedbackListQuery({
      scope: s.scope,
      status: status === 'any' ? undefined : [status],
      kind: s.kind ? [s.kind] : undefined,
      severity: s.severity ? [s.severity] : undefined,
      area: s.area ? [s.area] : undefined,
      source: s.source ? [s.source] : undefined,
      q: s.q || undefined,
    }),
  );
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  const set = (patch: Partial<z.infer<typeof search>>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const sync = useActionMutation(syncFeedback, {
    invalidate: [['dev', 'feedback']],
    success: (r) => t('feedback.summary.synced', { i: r.imported, c: r.checked, r: r.resolved }),
    onSuccess: (r) => {
      if (r.warning) toast.warning(r.warning);
    },
  });

  return (
    <>
      <PageHeader
        title={t('feedback.title')}
        description={t(manager ? 'feedback.list.descriptionAll' : 'feedback.list.descriptionMine')}
        actions={
          <>
            {manager && config.data?.forge.configured && (
              <Button variant="secondary" loading={sync.isPending} onClick={() => sync.mutate({ input: {} })}>
                <RefreshCw /> {t('feedback.summary.sync')}
              </Button>
            )}
            <Button onClick={() => setOpen(true)}>{t('feedback.new')}</Button>
          </>
        }
      />
      {manager && <Summary />}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('common.search')} className="w-72">
          {(p) => <Input {...p} value={s.q ?? ''} placeholder={t('feedback.list.search')} onChange={(e) => set({ q: e.target.value || undefined })} />}
        </Field>
        <Field label={t('common.status')} className="w-44">
          {(p) => (
            <NativeSelect {...p} value={status} onChange={(e) => set({ status: e.target.value as never })}>
              <option value="open">{t('feedback.list.open')}</option>
              <option value="any">{t('feedback.list.any')}</option>
              {FEEDBACK_STATUSES.map((st) => (
                <option key={st} value={st}>
                  {t(`feedback.status.${st}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('feedback.kindLabel')} className="w-40">
          {(p) => (
            <NativeSelect {...p} value={s.kind ?? ''} onChange={(e) => set({ kind: (e.target.value || undefined) as never })}>
              <option value="">{t('feedback.list.anyKind')}</option>
              {FEEDBACK_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`feedback.kind.${k}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        {manager && (
          <>
            <Field label={t('feedback.fields.severity')} className="w-36">
              {(p) => (
                <NativeSelect {...p} value={s.severity ?? ''} onChange={(e) => set({ severity: (e.target.value || undefined) as never })}>
                  <option value="">{t('feedback.list.anySeverity')}</option>
                  {FEEDBACK_SEVERITIES.map((v) => (
                    <option key={v} value={v}>
                      {t(`feedback.severity.${v}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field label={t('feedback.fields.area')} className="w-44">
              {(p) => (
                <NativeSelect {...p} value={s.area ?? ''} onChange={(e) => set({ area: (e.target.value || undefined) as never })}>
                  <option value="">{t('feedback.list.anyArea')}</option>
                  {FEEDBACK_AREAS.map((a) => (
                    <option key={a} value={a}>
                      {t(`feedback.area.${a}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field label={t('feedback.list.forge')} className="w-44">
              {(p) => (
                <NativeSelect {...p} value={s.scope === 'mine' ? 'mine' : (s.source ?? '')} onChange={(e) => set(e.target.value === 'mine' ? { scope: 'mine', source: undefined } : { scope: undefined, source: (e.target.value || undefined) as never })}>
                  <option value="">{t('feedback.list.anySource')}</option>
                  <option value="app">{t('feedback.source.app')}</option>
                  <option value="forge">{t('feedback.source.forge')}</option>
                  <option value="mine">{t('feedback.list.mine')}</option>
                </NativeSelect>
              )}
            </Field>
          </>
        )}
      </div>
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : q.isLoading ? (
        <Skeleton className="h-64" />
      ) : !rows.length ? (
        <EmptyState title={t('feedback.list.empty')} description={t('feedback.list.emptyHint')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th className="w-20">#</Th>
              <Th>{t('feedback.fields.title')}</Th>
              <Th className="hidden md:table-cell">{t('feedback.kindLabel')}</Th>
              <Th className="hidden lg:table-cell">{t('feedback.fields.area')}</Th>
              {manager && <Th className="hidden lg:table-cell">{t('feedback.list.reporter')}</Th>}
              <Th>{t('common.status')}</Th>
              {manager && <Th className="hidden md:table-cell">{t('feedback.list.forge')}</Th>}
              <Th className="hidden sm:table-cell">{t('feedback.list.reported')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((fb) => (
              <Tr key={fb.id}>
                <Td>
                  <Mono className="text-xs">{fb.ref}</Mono>
                </Td>
                <Td>
                  <Link to="/feedback/$id" params={{ id: fb.id }} className="font-bold hover:underline">
                    {fb.title}
                  </Link>
                  {fb.severity && (
                    <span className="ml-2 text-xs">
                      <SeverityText severity={fb.severity} />
                    </span>
                  )}
                </Td>
                <Td className="hidden md:table-cell">
                  <KindLabel kind={fb.kind} />
                </Td>
                <Td className="hidden lg:table-cell text-fg-2">{t(`feedback.area.${fb.area}`)}</Td>
                {manager && <Td className="hidden lg:table-cell text-fg-2">{fb.reporterLabel.replace(/ <.*>$/, '')}</Td>}
                <Td>
                  <StatusBadge status={fb.status} />
                </Td>
                {manager && (
                  <Td className="hidden md:table-cell">
                    {fb.forge.number ? (
                      <a href={fb.forge.url ?? undefined} target="_blank" rel="noreferrer" className="text-sm hover:underline">
                        #{fb.forge.number}
                      </a>
                    ) : fb.forge.error ? (
                      <Badge tone="danger">{t('feedback.list.notSent')}</Badge>
                    ) : (
                      <span className="text-fg-3">–</span>
                    )}
                  </Td>
                )}
                <Td className="hidden sm:table-cell whitespace-nowrap tabular text-fg-2">{formatDate(fb.createdAt)}</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      {q.hasNextPage && (
        <div className="mt-3 flex justify-center">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
      {open && <FeedbackDialog onClose={() => setOpen(false)} />}
    </>
  );
}

function Summary() {
  const { t } = useTranslation();
  const s = useQuery(feedbackSummaryQuery);
  if (!s.data) return null;
  const d = s.data;
  return (
    <div className="mb-5 flex flex-wrap gap-6">
      <div>
        <div className="text-xs text-fg-3">{t('feedback.summary.openBugs')}</div>
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold tabular">{d.open.bug}</span>
          {FEEDBACK_SEVERITIES.map((k) => d.openBugsBySeverity[k] > 0 && (
            <Badge key={k} tone={k === 'minor' ? 'neutral' : 'danger'}>
              {d.openBugsBySeverity[k]} {t(`feedback.severity.${k}`).toLowerCase()}
            </Badge>
          ))}
        </div>
      </div>
      {(['feature', 'question'] as const).map((k) => (
        <div key={k}>
          <div className="text-xs text-fg-3">{t(`feedback.kind.${k}`)}</div>
          <div className="text-lg font-bold tabular">{d.open[k]}</div>
        </div>
      ))}
      {d.notInForge > 0 && (
        <div>
          <div className="text-xs text-fg-3">{t('feedback.summary.notInForge')}</div>
          <div className="text-lg font-bold tabular text-danger">{d.notInForge}</div>
        </div>
      )}
    </div>
  );
}
